import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { admin, changePassword, logout } = useAdminAuth();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError("");

      if (!oldPassword.trim()) {
        setError("请输入当前密码");
        return;
      }
      if (!newPassword.trim()) {
        setError("请输入新密码");
        return;
      }
      if (newPassword.length < 6) {
        setError("新密码至少需要6位");
        return;
      }
      if (newPassword !== confirmPassword) {
        setError("两次输入的新密码不一致");
        return;
      }
      if (oldPassword === newPassword) {
        setError("新密码不能与旧密码相同");
        return;
      }

      setLoading(true);
      const res = await changePassword(oldPassword, newPassword);
      setLoading(false);

      if (res?.success) {
        // Update admin state to mark password as changed
        if (admin) {
          const updatedAdmin = { ...admin, must_change_password: false };
          localStorage.setItem("admin_user", JSON.stringify(updatedAdmin));
        }
        navigate("/admin/overview");
      } else {
        setError(res?.error || "修改密码失败，请重试");
      }
    },
    [oldPassword, newPassword, confirmPassword, changePassword, navigate, admin]
  );

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  // Prevent logged-out users from seeing this page
  if (!admin) {
    navigate("/admin/login");
    return null;
  }

  return (
    <div className="min-h-screen bg-[#f8f5f0] flex flex-col items-center justify-center px-5 py-8">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-10">
          <div className="w-20 h-20 rounded-2xl overflow-hidden mb-5 bg-white shadow-sm flex items-center justify-center p-2">
            <img
              src="/logo.png"
              alt="RTB Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-xl font-bold text-[#1a3a5c] tracking-wide">
            修改密码
          </h1>
          {admin.must_change_password && (
            <div className="mt-2 flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs rounded-lg px-3 py-2">
              <i className="ri-shield-keyhole-line"></i>
              <span>首次登录，请先修改密码</span>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-lg bg-[#1a3a5c] flex items-center justify-center">
              <i className="ri-lock-password-line text-white text-sm"></i>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[#1a3a5c]">密码修改</h2>
              <p className="text-[10px] text-gray-400">{admin.name} · {admin.username}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                当前密码
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder="请输入当前密码"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1a3a5c] placeholder:text-gray-300 focus:outline-none focus:border-[#c9a96e] focus:ring-1 focus:ring-[#c9a96e]/20 transition-all bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                新密码
              </label>
              <div className="relative">
                <i className="ri-key-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="请输入新密码（至少6位）"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1a3a5c] placeholder:text-gray-300 focus:outline-none focus:border-[#c9a96e] focus:ring-1 focus:ring-[#c9a96e]/20 transition-all bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                确认新密码
              </label>
              <div className="relative">
                <i className="ri-key-2-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="请再次输入新密码"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1a3a5c] placeholder:text-gray-300 focus:outline-none focus:border-[#c9a96e] focus:ring-1 focus:ring-[#c9a96e]/20 transition-all bg-white"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-1.5 text-red-500 text-xs bg-red-50 rounded-lg px-3 py-2">
                <i className="ri-error-warning-line"></i>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#1a3a5c] hover:bg-[#142d48] text-white font-semibold py-3 rounded-xl transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-sm"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  修改中...
                </>
              ) : (
                <>
                  <i className="ri-check-line"></i>
                  确认修改
                </>
              )}
            </button>
          </form>

          {!admin.must_change_password && (
            <div className="mt-4 pt-4 border-t border-gray-100 text-center">
              <button
                onClick={() => navigate("/admin/overview")}
                className="text-[11px] text-[#c9a96e] hover:underline cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-1"></i>
                返回系统
              </button>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-gray-100 text-center">
            <button
              onClick={handleLogout}
              className="text-[11px] text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-r-line mr-1"></i>
              退出登录
            </button>
          </div>
        </div>
      </div>

      <footer className="mt-auto pt-8 pb-4 text-center w-full">
        <p className="text-[11px] text-gray-400">RTB 试管助孕中心</p>
        <p className="text-[10px] text-gray-300 mt-0.5">客户全周期管理 CRM 系统</p>
        <p className="text-[10px] text-gray-300 mt-1">&copy; 2025 RTB Fertility. All Rights Reserved.</p>
      </footer>
    </div>
  );
}